import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Gift, Copy, Check, Share2, Crown, Trophy } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Progress } from "./ui/progress";
import { authFetch } from "../lib/auth";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReferralCard = ({ sub }) => {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${API}/referral/me`);
        if (res.ok && !cancelled) {
          setData(await res.json());
        }
      } catch (e) {
        // silent
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!data) return null;
  // Hide for memory_school members — they don't need bonus days
  if (sub?.subscription_status === "memory_school") return null;

  const shareUrl = `${window.location.origin}/?ref=${data.referral_code}`;
  const bonus = data.bonus_days_per_referral;
  const earned = data.bonus_days_earned;
  const completed = data.completed_count;
  const threshold = data.lifetime_threshold ?? 3;
  const lifetimeUnlocked = !!data.lifetime_unlocked;
  const lifetimeRemaining = data.lifetime_remaining ?? Math.max(0, threshold - completed);
  const milestoneProgress = Math.min(100, (completed / threshold) * 100);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Could not copy. Long-press the link to copy.");
    }
  };

  const nativeShare = async () => {
    const milestoneText = lifetimeUnlocked
      ? `Sharpen your memory with MindForge — sign up with my link and we both get an extra ${bonus} days free!`
      : `Sharpen your memory with MindForge — sign up with my link and we both get an extra ${bonus} days free!`;
    const shareData = {
      title: "Join me on MindForge",
      text: milestoneText,
      url: shareUrl,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (e) {
        // user cancelled
      }
    } else {
      copyLink();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
      className="mb-12"
      data-testid="referral-card"
    >
      <Card className={`bg-card border-border overflow-hidden ${lifetimeUnlocked ? 'ring-2 ring-primary/40' : ''}`}>
        <CardContent className="p-0">
          <div className="grid grid-cols-1 md:grid-cols-3">
            <div className="md:col-span-2 p-6 md:p-8">
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 flex items-center justify-center rounded-full ${lifetimeUnlocked ? 'bg-primary/20' : 'bg-secondary/15'}`}>
                  {lifetimeUnlocked ? (
                    <Crown className="w-5 h-5 text-primary" />
                  ) : (
                    <Gift className="w-5 h-5 text-secondary" />
                  )}
                </div>
                <h3 className="font-heading text-xl md:text-2xl font-semibold">
                  {lifetimeUnlocked
                    ? "You unlocked MindForge for life 👑"
                    : `Invite ${threshold} friends → MindForge free for life`}
                </h3>
              </div>
              <p className="text-muted-foreground text-sm md:text-base mb-5">
                {lifetimeUnlocked ? (
                  <>You got <span className="text-foreground font-medium">{completed} friends</span> to join — your subscription is now comped permanently. Keep sharing — they still get +{bonus} bonus days each.</>
                ) : (
                  <>Every friend who joins via your link gets <span className="text-foreground font-medium">+{bonus} days free</span> — and so do you. Get <span className="text-primary font-medium">{threshold} friends</span> in and your subscription is on us, forever.</>
                )}
              </p>

              {/* Milestone progress bar */}
              {!lifetimeUnlocked && (
                <div className="mb-5" data-testid="milestone-progress-section">
                  <div className="flex items-center justify-between mb-1.5 text-xs">
                    <span className="text-muted-foreground flex items-center gap-1.5">
                      <Trophy className="w-3.5 h-3.5" />
                      Free-for-life progress
                    </span>
                    <span className="font-mono text-foreground" data-testid="milestone-progress-text">
                      {completed} / {threshold} friends
                    </span>
                  </div>
                  <Progress value={milestoneProgress} className="h-2" data-testid="milestone-progress-bar" />
                  <p className="text-xs text-muted-foreground mt-1.5">
                    {lifetimeRemaining === 1
                      ? "Just 1 more friend to unlock free for life!"
                      : `${lifetimeRemaining} more to unlock free for life`}
                  </p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-2 mb-3">
                <div
                  className="flex-1 bg-background border border-border px-3 py-2.5 font-mono text-xs md:text-sm truncate select-all"
                  data-testid="referral-link"
                >
                  {shareUrl}
                </div>
                <Button
                  onClick={copyLink}
                  data-testid="referral-copy-btn"
                  variant="outline"
                  className="sm:w-auto"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 mr-2 text-primary" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy link
                    </>
                  )}
                </Button>
                <Button
                  onClick={nativeShare}
                  data-testid="referral-share-btn"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 sm:w-auto"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Your code: <span className="font-mono text-foreground">{data.referral_code}</span>
              </p>
            </div>

            <div className={`border-t md:border-t-0 md:border-l border-border p-6 md:p-8 flex flex-col justify-center ${lifetimeUnlocked ? 'bg-primary/10' : 'bg-primary/5'}`}>
              {lifetimeUnlocked ? (
                <>
                  <p className="text-xs uppercase tracking-wider text-primary mb-2 flex items-center gap-1.5">
                    <Crown className="w-3.5 h-3.5" />
                    Lifetime access
                  </p>
                  <div className="text-3xl md:text-4xl font-bold text-primary font-heading mb-1" data-testid="lifetime-badge">
                    Free forever
                  </div>
                  <p className="text-sm text-muted-foreground" data-testid="referral-completed-count">
                    {completed} friends joined · {earned} bonus days earned
                  </p>
                </>
              ) : (
                <>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                    Your rewards
                  </p>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span
                      className="text-5xl font-bold text-primary font-heading"
                      data-testid="referral-bonus-days"
                    >
                      {earned}
                    </span>
                    <span className="text-muted-foreground">
                      bonus day{earned === 1 ? "" : "s"}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground" data-testid="referral-completed-count">
                    from {completed} friend{completed === 1 ? "" : "s"} joined
                  </p>
                  {data.pending_count > 0 && (
                    <p className="text-xs text-muted-foreground mt-2">
                      {data.pending_count} invite
                      {data.pending_count === 1 ? "" : "s"} waiting to verify
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default ReferralCard;
